import { BoxSearch } from '@/components/BoxSearch';
import GoBackMenu from '@/components/GoBackNavbar';
import Constants from '@/Constants';
import { ApplicationState } from '@/store';
import { getCategoriesClear, getCategoriesRequest } from '@/store/category/action';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CategoryList } from './CategoryList';
import { NewCategoryModal } from './NewCategoryModal';
import './styles.scss';

const PAGE_SIZE = 10;

type ParamsDataType = {
    showOnlyInactive: boolean;
    currentPage: number;
    search: string;
};

const INITIAL_PARAMS_DATA: ParamsDataType = {
    showOnlyInactive: false,
    currentPage: 1,
    search: '',
};

const CategoryManagement: React.FC = () => {
    const boxSearchRef = useRef<BoxSearch>(null);
    const newCategoryModalRef = useRef<NewCategoryModal>(null);

    const [paramsData, setParamsData] = useState(INITIAL_PARAMS_DATA);

    const dispatch = useDispatch();

    const { strings, organizationId } = useSelector((state: ApplicationState) => ({
        strings: state.locale.data,
        organizationId: state.appOrganization.data?.organization?.id,
    }));

    const toggleShowOnlyInativeFilter = () => {
        setParamsData((prev) => ({
            ...prev,
            currentPage: 1,
            showOnlyInactive: !prev.showOnlyInactive,
        }));
    };

    const openCreateNewCategoryModal = () => {
        newCategoryModalRef.current?.show();
    };

    const onSearchChange = (value: string) => {
        setParamsData((prev) => ({
            ...prev,
            currentPage: 1,
            search: value,
        }));
    };

    const onPageChage = (page: number) => {
        setParamsData((prev) => ({
            ...prev,
            currentPage: page,
        }));
    };

    const loadCategories = useCallback(() => {
        dispatch(
            getCategoriesRequest({
                applicationId: Constants.APP_ID,
                orgId: organizationId,
                locale: strings._LANGUAGE as any,
                onlyInactive: paramsData.showOnlyInactive,
                limit: PAGE_SIZE,
                offset: PAGE_SIZE * (paramsData.currentPage - 1),
                name: paramsData.search,
            }),
        );

        return () => {
            dispatch(getCategoriesClear());
        };
    }, [dispatch, organizationId, strings, paramsData]);

    const onUpdateCategoryList = useCallback(() => {
        loadCategories();
    }, [loadCategories]);

    const onCreatedSuccess = useCallback(() => {
        setParamsData(INITIAL_PARAMS_DATA);
    }, []);

    useEffect(() => {
        loadCategories();

        return () => {
            dispatch(getCategoriesClear());
        };
    }, [dispatch, organizationId, strings, paramsData]);

    return (
        <>
            <GoBackMenu goBackPath="/config" />
            <div className="category-management-container container">
                <div className="content-header">
                    <h1>{strings.CATEGORY_MANAGEMENT}</h1>
                    <span>{strings.CATEGORY_MANAGEMENT_DESCRIPTION}</span>

                    <div className="actions-container">
                        <button className="btn btn-primary btn-sm create-new-category-btn" onClick={openCreateNewCategoryModal}>
                            {strings.CATEGORY_MANAGEMENT_ADD_NEW_CATEGORY}
                        </button>
                        <BoxSearch
                            delay={500}
                            minChars={2}
                            onChange={onSearchChange}
                            placeholder={strings.CATEGORY_MANAGEMENT_SEARCH_BOX_PLACEHOLDER}
                            ref={boxSearchRef}
                        />
                    </div>

                    <div className="filters-container">
                        <span>{strings.FILTER_BY}:</span>
                        <div className="filter-itens-container">
                            <button
                                className={`btn btn-primary btn-sm filter ${paramsData.showOnlyInactive && 'is-active'}`}
                                onClick={toggleShowOnlyInativeFilter}
                                type="button"
                            >
                                {strings.CATEGORY_MANAGEMENT_FILTER_INACTIVE}
                            </button>
                        </div>
                    </div>
                </div>

                <CategoryList
                    currentPage={paramsData.currentPage}
                    handleCreateNewCategory={openCreateNewCategoryModal}
                    onPageChange={onPageChage}
                    onUpdateCategoryList={onUpdateCategoryList}
                    registersPerPage={PAGE_SIZE}
                    showOnlyInactive={paramsData.showOnlyInactive}
                />

                <NewCategoryModal onCreatedSuccess={onCreatedSuccess} ref={newCategoryModalRef} />
            </div>
        </>
    );
};

export { CategoryManagement };
